import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { FullPageLoader } from '@/components/ui/full-page-loader'
import { useAuth } from '@/features/auth'

import { paths } from './paths'

interface RequireAuthProps {
  children: ReactNode
  /** When set, the user must hold at least one of these roles. */
  roles?: string[]
  /** Skip the "must change password" redirect (used by the change-password page). */
  allowPasswordChangePending?: boolean
}

export function RequireAuth({
  children,
  roles = [],
  allowPasswordChangePending = false,
}: RequireAuthProps) {
  const { user, isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) return <FullPageLoader label="Restoring your session…" />

  if (!isAuthenticated || !user) {
    // Remember where they were headed so login can send them back.
    return <Navigate to={paths.login} state={{ from: location }} replace />
  }

  if (user.mustChangePassword && !allowPasswordChangePending) {
    return <Navigate to={paths.changePassword} replace />
  }

  if (roles.length > 0 && !user.roles.some((role) => roles.includes(role))) {
    return <Navigate to={paths.dashboard} replace />
  }

  return <>{children}</>
}
